import { CheckIcon, ChevronsUpDown } from 'lucide-react'
import { ReactNode, useState } from 'react'
import { Control, FieldPath, FieldValues } from 'react-hook-form'

import { cn } from '@/lib/utils'

import { Button } from '../../ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../ui/command'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '../../ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover'
import { Label } from './label'

// prettier-ignore
export interface SelectProps<
  TFieldValues extends FieldValues             = FieldValues,
  TName        extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  field_control:      Control<TFieldValues>
  field_name:         TName
  field_label?:       ReactNode
  field_description?: ReactNode
  field_options:      {
    value:    TFieldValues[TName]
    label:    string[]
    content?: ReactNode
  }[]
  field_labelSeparator?: string
}

const Component = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  field_control,
  field_name,
  field_label,
  field_description,
  field_options,
}: SelectProps<TFieldValues, TName>) => {
  const [open, setOpen] = useState(false)

  return (
    <FormField
      control={field_control}
      name={field_name}
      render={({ field: { value, onChange } }) => (
        <FormItem className="flex flex-col">
          {field_label && <Label>{field_label}</Label>}

          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant="outline"
                  role="combobox"
                  className={cn(
                    'w-full justify-between',
                    !value && 'text-muted-foreground',
                  )}
                >
                  {value
                    ? field_options.find((option) => option.value === value)
                        ?.label[0]
                    : 'Selecionar...'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </FormControl>
            </PopoverTrigger>

            <PopoverContent className="w-full p-0">
              <Command>
                <CommandInput placeholder="Pesquisar..." className="h-9" />
                <CommandList>
                  <CommandEmpty>Nenhum item encontrado</CommandEmpty>
                  <CommandGroup>
                    {field_options.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.label.join('||')}
                        onSelect={() => {
                          onChange(option.value)
                          setOpen(false)
                        }}
                      >
                        {option.content ?? option.label.join(', ')}
                        <CheckIcon
                          className={cn(
                            'ml-auto h-4 w-4',
                            option.value === value
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {field_description && (
            <FormDescription>{field_description}</FormDescription>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

Component.displayName = 'Select'

export { Component as Select }
